import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { Textarea } from "@fieldwork/ui/components/textarea";
import { cn } from "@fieldwork/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { NotConnected, useServer } from "@/components/server-hooks";
import { Note, Panel, ServerPage } from "@/components/server-shell";
import { Field, Select } from "@/components/ui-bits";
import { useAgentAction, useAgentQuery } from "@/lib/act";

export const Route = createFileRoute("/_auth/servers/$id/workflow")({
  component: WorkflowTab,
});

type Owner = { name?: string; short_name?: string; match_names?: string[]; timezone?: string; about?: string };
type Sla = {
  enabled?: boolean;
  hours?: number;
  work_days?: number[];
  work_hours?: string[];
  project?: string;
  label?: string;
  company_file?: string;
  exclude_chats?: string[];
};
type Workflow = { owner: Owner; me: string; me_exists: boolean; sla: Sla };

const zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["UTC"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function WorkflowTabBody() {
  const { id } = Route.useParams();
  const server = useServer(id).data;
  const connected = server?.hub.connected;
  const q = useAgentQuery<Workflow>(id, "workflow_get", {}, !!connected);
  const projects = useAgentQuery<{ projects: { slug: string; name: string }[] }>(id, "context_list", {}, !!connected);
  const [owner, setOwner] = useState<Owner>({});
  const [me, setMe] = useState("");
  const [sla, setSla] = useState<Sla>({});
  useEffect(() => {
    if (q.data) {
      setOwner(q.data.owner);
      setMe(q.data.me);
      setSla(q.data.sla);
    }
  }, [q.data]);
  const save = useAgentAction<Record<string, unknown>, { saved: string[] }>(id, "workflow_save", (v) => v, {
    refresh: ["workflow_get"],
    success: (r) => `Saved ${r.saved.join(", ")}; the next run uses it`,
  });

  if (!connected) return <NotConnected />;
  if (q.error) return <p className="text-sm text-[#F48A80]">{q.error.message}</p>;
  if (!q.data) return <p className="text-sm text-[#A8A59D]">Loading…</p>;
  const o = (k: keyof Owner) => (e: { target: { value: string } }) => setOwner({ ...owner, [k]: e.target.value });

  return (
    <>
      <Panel title="About you" className="gap-4">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-4">
          <Field label="Name">
            <Input className="h-11" value={owner.name ?? ""} onChange={o("name")} />
          </Field>
          <Field label="Short name" hint="How the agent refers to you">
            <Input className="h-11" value={owner.short_name ?? ""} onChange={o("short_name")} />
          </Field>
          <Field label="Timezone">
            <Select className="h-11" value={owner.timezone ?? "UTC"} onChange={o("timezone")}>
              {zones.map((z) => (
                <option key={z}>{z}</option>
              ))}
            </Select>
          </Field>
          <Field label="Names in meeting notes" hint="Comma-separated, e.g. ada, ada l">
            <Input
              className="h-11"
              value={(owner.match_names ?? []).join(", ")}
              onChange={(e) => setOwner({ ...owner, match_names: e.target.value.split(",").map((x) => x.trim()) })}
            />
          </Field>
        </div>
        <Field label="One paragraph about your work" hint="Read by every classifier: your roles, companies, what you're responsible for.">
          <Textarea rows={3} value={owner.about ?? ""} onChange={o("about")} />
        </Field>
        <div>
          <Button
            disabled={save.isPending}
            onClick={() => save.mutate({ owner: { ...owner, match_names: (owner.match_names ?? []).filter(Boolean) } })}
          >
            Save profile
          </Button>
        </div>
      </Panel>

      <Panel title="Your rules">
        <p className="m-0 text-[#CFCBC1]">
          In your own words: what matters most, whose asks come first, what to ignore, how you like tasks named. The agent
          reads this on every run. Company-specific rules go in that company's notes (Projects).
        </p>
        <label className="flex flex-col">
          <span className="sr-only">Rules</span>
          <Textarea
            rows={12}
            className="bg-[#0E0E0D] px-4 py-3.5 font-mono text-[13px] leading-[1.7]"
            value={me}
            onChange={(e) => setMe(e.target.value)}
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Note>Saved on your server as a git commit, so a change can be undone.</Note>
          <Button disabled={save.isPending} onClick={() => save.mutate({ me })}>
            Save rules
          </Button>
        </div>
      </Panel>

      <Panel
        className="gap-4"
        title="Partner reply times"
        actions={
          <label className="flex items-center gap-2.5 text-sm">
            <input
              type="checkbox"
              className="size-[18px] accent-[#FF4F1A]"
              checked={!!sla.enabled}
              onChange={(e) => setSla({ ...sla, enabled: e.target.checked })}
            />
            Watch partner reply times
          </label>
        }
      >
        <p className="m-0 text-[#CFCBC1]">
          For work where partners expect a quick first reply: when a partner's message has waited longer than this, in working
          hours, the agent raises an urgent task and alerts you.
        </p>
        {sla.enabled && (
          <>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-4">
              <Field label="Reply within">
                <span className="flex items-center gap-2">
                  <Input
                    className="h-11 w-[90px] font-mono"
                    type="number"
                    min={1}
                    max={72}
                    value={sla.hours ?? 3}
                    onChange={(e) => setSla({ ...sla, hours: Number(e.target.value) })}
                  />
                  <span className="text-[#A8A59D]">working hours</span>
                </span>
              </Field>
              <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
                <legend className="pb-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">Working hours</legend>
                <span className="flex items-center gap-2">
                  <Input
                    className="h-11 px-2.5"
                    type="time"
                    aria-label="From"
                    value={sla.work_hours?.[0] ?? "09:00"}
                    onChange={(e) => setSla({ ...sla, work_hours: [e.target.value, sla.work_hours?.[1] ?? "19:00"] })}
                  />
                  <span className="text-[#A8A59D]">to</span>
                  <Input
                    className="h-11 px-2.5"
                    type="time"
                    aria-label="To"
                    value={sla.work_hours?.[1] ?? "19:00"}
                    onChange={(e) => setSla({ ...sla, work_hours: [sla.work_hours?.[0] ?? "09:00", e.target.value] })}
                  />
                </span>
              </fieldset>
              <Field label="Project for partner tasks">
                <Select className="h-11" value={sla.project ?? ""} onChange={(e) => setSla({ ...sla, project: e.target.value })}>
                  <option value="">choose…</option>
                  {projects.data?.projects.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
              <legend className="pb-2 font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">Working days</legend>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d, i) => {
                  const on = (sla.work_days ?? [0, 1, 2, 3, 4]).includes(i);
                  return (
                    <label
                      key={d}
                      className={cn(
                        "flex min-h-9 cursor-pointer items-center gap-1.5 rounded-[4px] px-3 text-[13px]",
                        on ? "bg-[#222220] shadow-[inset_0_0_0_1px_#FF4F1A]" : "bg-[#161615] text-[#A8A59D] shadow-[inset_0_0_0_1px_#34332F]",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="accent-[#FF4F1A]"
                        checked={on}
                        onChange={(e) => {
                          const cur = new Set(sla.work_days ?? [0, 1, 2, 3, 4]);
                          if (e.target.checked) cur.add(i);
                          else cur.delete(i);
                          setSla({ ...sla, work_days: [...cur].sort() });
                        }}
                      />
                      {d}
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <Field
              label="Chats to never count"
              hint="Exact chat names, comma-separated. Which chats count as partner chats is set in each company's notes."
            >
              <Input
                className="h-11"
                value={(sla.exclude_chats ?? []).join(", ")}
                onChange={(e) => setSla({ ...sla, exclude_chats: e.target.value.split(",").map((x) => x.trim()) })}
              />
            </Field>
          </>
        )}
        <div>
          <Button
            disabled={save.isPending}
            onClick={() => {
              const clean: Sla = { ...sla, exclude_chats: (sla.exclude_chats ?? []).filter(Boolean) };
              if (clean.enabled && !clean.project) delete clean.project;
              save.mutate({ sla: clean });
            }}
          >
            Save reply times
          </Button>
        </div>
      </Panel>
    </>
  );
}

function WorkflowTab() {
  return (
    <ServerPage narrow eyebrow="Settings" title="How I work">
      <WorkflowTabBody />
    </ServerPage>
  );
}
